/**
 * Automation Landing Page
 * Generate personalised client documents from Word templates.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, Sparkles, Layers, Clock, Upload, Download, Plus, Loader2, Pencil, Check,
  AlertTriangle, Send, Mail, ChevronRight, Users, X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import apiClient from '@/services/api/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import UploadTemplateDialog from './UploadTemplateDialog';
import UploadLetterheadDialog from './UploadLetterheadDialog';
import SendEmailDialog from './SendEmailDialog';
import EditTemplateDialog from './EditTemplateDialog';

// ==================== Types ====================

interface TemplateVariable {
  name: string;
  isUserPrompted?: boolean;
  resolverSource?: string;
}

interface Template {
  id: string;
  name: string;
  category?: string;
  description?: string;
  variables?: TemplateVariable[];
  variableCount?: number;
  autoFillCount?: number;
  userInputCount?: number;
  createdAt?: string;
  originalFilename?: string;
}

interface Letterhead {
  id: string;
  name: string;
  isDefault?: boolean;
  hasContentPlaceholder?: boolean;
  createdAt?: string;
  originalFilename?: string;
}

interface GeneratedDocument {
  id: string;
  templateId?: string;
  templateName?: string;
  clientId?: string;
  clientName?: string;
  filename?: string;
  docxUrl?: string;
  pdfUrl?: string;
  emailStatus?: 'not_sent' | 'sent' | 'failed';
  createdAt?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  engagement_letter: 'Engagement Letter',
  tax_return_confirmation: 'Tax Return Confirmation',
  income_estimate: 'Income Estimate',
  minutes: 'Minutes',
  solvency_resolution: 'Solvency Resolution',
  meeting_minutes: 'Meeting Minutes',
  compliance_letter: 'Compliance Letter',
  fee_notice: 'Fee Notice',
  general: 'General',
};

function formatDate(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Australia/Sydney',
  });
}

/**
 * The backend returns snake_case from PostgreSQL. Normalise to camelCase
 * without losing the original keys (so either can be read).
 */
function normaliseGenerated(raw: any): GeneratedDocument {
  return {
    id: raw.id,
    templateId: raw.templateId ?? raw.template_id,
    templateName: raw.templateName ?? raw.template_name,
    clientId: raw.clientId ?? raw.client_id,
    clientName: raw.clientName ?? raw.client_name,
    filename: raw.filename,
    docxUrl: raw.docxUrl ?? raw.docx_url,
    pdfUrl: raw.pdfUrl ?? raw.pdf_url,
    emailStatus: raw.emailStatus ?? raw.email_status,
    createdAt: raw.createdAt ?? raw.created_at ?? raw.generated_at,
  };
}

function useDownloadUrl() {
  return async (path: string) => {
    try {
      const resp = await apiClient.get(path);
      const url = resp.data?.data?.url || resp.data?.url;
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Download link not available');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to fetch download link');
    }
  };
}

// ==================== Page ====================

export default function AutomationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'generate';
  const [activeTab, setActiveTab] = useState<string>(tabParam);
  const [uploadTemplateOpen, setUploadTemplateOpen] = useState(false);
  const [uploadLetterheadOpen, setUploadLetterheadOpen] = useState(false);

  const onTabChange = (v: string) => {
    setActiveTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'generate') next.delete('tab'); else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        icon={FileText}
        title="Automation"
        description="Generate personalised client documents using Word templates"
        actions={
          activeTab === 'templates' ? (
            <Button size="sm" onClick={() => setUploadTemplateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> New Template
            </Button>
          ) : activeTab === 'letterheads' ? (
            <Button size="sm" onClick={() => setUploadLetterheadOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> New Letterhead
            </Button>
          ) : (
            <Button size="sm" onClick={() => navigate('/panel/automation/generate')}>
              <Sparkles className="mr-2 h-4 w-4" /> Start New Document
            </Button>
          )
        }
      />

      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="generate"><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Generate</TabsTrigger>
          <TabsTrigger value="templates"><FileText className="mr-1.5 h-3.5 w-3.5" /> Templates</TabsTrigger>
          <TabsTrigger value="letterheads"><Layers className="mr-1.5 h-3.5 w-3.5" /> Letterheads</TabsTrigger>
          <TabsTrigger value="history"><Clock className="mr-1.5 h-3.5 w-3.5" /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="generate"><GenerateTab onNew={() => navigate('/panel/automation/generate')} /></TabsContent>
        <TabsContent value="templates"><TemplatesTab onUpload={() => setUploadTemplateOpen(true)} /></TabsContent>
        <TabsContent value="letterheads"><LetterheadsTab onUpload={() => setUploadLetterheadOpen(true)} /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
      </Tabs>

      <UploadTemplateDialog open={uploadTemplateOpen} onOpenChange={setUploadTemplateOpen} />
      <UploadLetterheadDialog open={uploadLetterheadOpen} onOpenChange={setUploadLetterheadOpen} />
    </div>
  );
}

// ==================== Generate Tab ====================

function GenerateTab({ onNew }: { onNew: () => void }) {
  const { data: generatedResp, isLoading } = useQuery({
    queryKey: ['automation', 'generated', 'recent'],
    queryFn: async () => {
      try {
        const resp = await apiClient.get('/automation/generated?limit=5');
        const body = resp.data?.data || resp.data || [];
        const rows = Array.isArray(body) ? body : (body.items || body.data || []);
        return rows.map(normaliseGenerated);
      } catch {
        return [];
      }
    },
  });

  const recents: GeneratedDocument[] = (generatedResp as any) || [];

  return (
    <div className="space-y-4">
      <Card className="border-dashed border-2 border-[#5048e5]/40 bg-gradient-to-br from-[#5048e5]/5 to-transparent">
        <CardContent className="py-8 flex flex-col items-center justify-center gap-3 text-center">
          <div className="h-12 w-12 rounded-full bg-[#5048e5]/10 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-[#5048e5]" />
          </div>
          <div>
            <h3 className="text-base font-semibold">Generate a personalised document</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Pick a template, choose a client, and let AI fill the variables.</p>
          </div>
          <Button onClick={onNew} size="lg">
            <Sparkles className="mr-2 h-4 w-4" /> Start New Document
          </Button>
        </CardContent>
      </Card>

      <div>
        <h3 className="text-sm font-semibold mb-2">Recently generated</h3>
        {isLoading ? (
          <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
        ) : recents.length === 0 ? (
          <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">No documents generated yet.</CardContent></Card>
        ) : (
          <div className="grid gap-2">
            {recents.map(doc => <GeneratedDocumentRow key={doc.id} doc={doc} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function GeneratedDocumentRow({ doc }: { doc: GeneratedDocument }) {
  const download = useDownloadUrl();
  const [emailOpen, setEmailOpen] = useState(false);

  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-3">
        <div className="h-9 w-9 rounded bg-indigo-500/10 text-indigo-500 flex items-center justify-center shrink-0">
          <FileText className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{doc.templateName || doc.filename || 'Generated document'}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {doc.clientName || 'Unknown client'} · {formatDateTime(doc.createdAt)}
            {doc.emailStatus === 'sent' && <span className="ml-2 text-green-600">· Emailed</span>}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => download(`/automation/generated/${doc.id}/download-pdf`)}>
            <Download className="h-3.5 w-3.5 mr-1" /> PDF
          </Button>
          <Button variant="ghost" size="sm" onClick={() => download(`/automation/generated/${doc.id}/download-docx`)}>
            <Download className="h-3.5 w-3.5 mr-1" /> Word
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEmailOpen(true)}>
            <Send className="h-3.5 w-3.5 mr-1" /> Email
          </Button>
        </div>
      </CardContent>
      <SendEmailDialog open={emailOpen} onOpenChange={setEmailOpen} generatedId={doc.id} filename={doc.filename} />
    </Card>
  );
}

// ==================== Templates Tab ====================

function TemplatesTab({ onUpload }: { onUpload: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const download = useDownloadUrl();
  const [editTarget, setEditTarget] = useState<Template | null>(null);

  const { data: templates = [], isLoading } = useQuery<Template[]>({
    queryKey: ['automation', 'templates'],
    queryFn: async () => {
      try {
        const resp = await apiClient.get('/automation/templates');
        const body = resp.data?.data || resp.data || [];
        return Array.isArray(body) ? body : (body.items || []);
      } catch {
        return [];
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/automation/templates/${id}`),
    onSuccess: () => {
      toast.success('Template deleted');
      queryClient.invalidateQueries({ queryKey: ['automation', 'templates'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to delete'),
  });

  if (isLoading) {
    return <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">Loading templates…</CardContent></Card>;
  }

  if (templates.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
          <FileText className="h-10 w-10 text-muted-foreground" />
          <div>
            <p className="font-medium">No templates yet</p>
            <p className="text-sm text-muted-foreground">Upload a Word template with variable placeholders to get started.</p>
          </div>
          <Button onClick={onUpload}><Upload className="mr-2 h-4 w-4" /> Upload Template</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {templates.map((tmpl) => {
        const vars = tmpl.variables || [];
        const userCount = tmpl.userInputCount ?? vars.filter(v => v.isUserPrompted).length;
        const autoCount = tmpl.autoFillCount ?? (vars.length - userCount);
        const totalVars = tmpl.variableCount ?? vars.length;
        return (
          <Card key={tmpl.id} className="flex flex-col">
            <CardHeader className="pb-2">
              <div className="flex items-start gap-2">
                <div className="h-9 w-9 rounded bg-blue-500/10 text-blue-500 flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-sm truncate">{tmpl.name}</CardTitle>
                  {tmpl.category && (
                    <Badge variant="outline" className="text-[10px] mt-1">{CATEGORY_LABELS[tmpl.category] || tmpl.category}</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 flex-1 space-y-2">
              {tmpl.description && <p className="text-xs text-muted-foreground line-clamp-2">{tmpl.description}</p>}
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="secondary" className="text-[10px]">{totalVars} vars</Badge>
                <span className="text-muted-foreground">{autoCount} auto · {userCount} user input</span>
              </div>
              <p className="text-[11px] text-muted-foreground">Created {formatDate(tmpl.createdAt)}</p>
            </CardContent>
            <CardContent className="pt-0 flex items-center gap-1 border-t pt-2">
              <Button size="sm" className="flex-1" onClick={() => navigate(`/panel/automation/generate/${tmpl.id}`)}>
                <Sparkles className="h-3.5 w-3.5 mr-1" /> Use
              </Button>
              <Button size="sm" variant="ghost" title="Edit" onClick={() => setEditTarget(tmpl)}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" title="Download" onClick={() => download(`/automation/templates/${tmpl.id}/download`)}>
                <Download className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                title="Delete"
                className="text-destructive hover:text-destructive"
                onClick={() => {
                  if (confirm(`Delete template "${tmpl.name}"?`)) deleteMutation.mutate(tmpl.id);
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </CardContent>
          </Card>
        );
      })}

      <EditTemplateDialog
        open={!!editTarget}
        onOpenChange={(o) => { if (!o) setEditTarget(null); }}
        template={editTarget}
      />
    </div>
  );
}

// ==================== Letterheads Tab ====================

function LetterheadsTab({ onUpload }: { onUpload: () => void }) {
  const queryClient = useQueryClient();
  const download = useDownloadUrl();

  const { data: letterheads = [], isLoading } = useQuery<Letterhead[]>({
    queryKey: ['automation', 'letterheads'],
    queryFn: async () => {
      try {
        const resp = await apiClient.get('/automation/letterheads');
        const body = resp.data?.data || resp.data || [];
        return Array.isArray(body) ? body : (body.items || []);
      } catch {
        return [];
      }
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: string) => apiClient.put(`/automation/letterheads/${id}`, { isDefault: true }),
    onSuccess: () => {
      toast.success('Default letterhead updated');
      queryClient.invalidateQueries({ queryKey: ['automation', 'letterheads'] });
    },
    onError: () => toast.error('Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/automation/letterheads/${id}`),
    onSuccess: () => {
      toast.success('Letterhead deleted');
      queryClient.invalidateQueries({ queryKey: ['automation', 'letterheads'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to delete'),
  });

  if (isLoading) {
    return <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">Loading letterheads…</CardContent></Card>;
  }

  if (letterheads.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
          <Layers className="h-10 w-10 text-muted-foreground" />
          <div>
            <p className="font-medium">No letterheads yet</p>
            <p className="text-sm text-muted-foreground">Upload a Word letterhead with a {'{{content}}'} placeholder.</p>
          </div>
          <Button onClick={onUpload}><Upload className="mr-2 h-4 w-4" /> Upload Letterhead</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {letterheads.map(lh => (
        <Card key={lh.id} className="flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex items-start gap-2">
              <div className="h-9 w-9 rounded bg-purple-500/10 text-purple-500 flex items-center justify-center shrink-0">
                <Layers className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-sm truncate">{lh.name}</CardTitle>
                <div className="flex items-center gap-1 mt-1">
                  {lh.isDefault && <Badge className="text-[10px] bg-green-500/10 text-green-600 border-green-500/30">Default</Badge>}
                  {lh.hasContentPlaceholder === false && (
                    <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/30 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> No {'{{content}}'}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 flex-1">
            <p className="text-[11px] text-muted-foreground">Created {formatDate(lh.createdAt)}</p>
          </CardContent>
          <CardContent className="pt-0 flex items-center gap-1 border-t pt-2">
            {!lh.isDefault && (
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setDefaultMutation.mutate(lh.id)}>
                <Check className="h-3.5 w-3.5 mr-1" /> Set Default
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => download(`/automation/letterheads/${lh.id}/download`)}>
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => {
                if (confirm(`Delete letterhead "${lh.name}"?`)) deleteMutation.mutate(lh.id);
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ==================== History Tab ====================

function HistoryTab() {
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const download = useDownloadUrl();

  const { data: templates = [] } = useQuery<Template[]>({
    queryKey: ['automation', 'templates'],
    queryFn: async () => {
      try {
        const resp = await apiClient.get('/automation/templates');
        const body = resp.data?.data || resp.data || [];
        return Array.isArray(body) ? body : (body.items || []);
      } catch {
        return [];
      }
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['automation', 'clients-for-filter'],
    queryFn: async () => {
      try {
        const resp = await apiClient.get('/clients?limit=500');
        const body = resp.data?.data || resp.data;
        return body?.clients || body?.items || body || [];
      } catch {
        return [];
      }
    },
  });

  const queryClient = useQueryClient();
  const { data: generated = [], isLoading } = useQuery<GeneratedDocument[]>({
    queryKey: ['automation', 'generated', clientFilter, templateFilter],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (clientFilter !== 'all') params.set('client_id', clientFilter);
        if (templateFilter !== 'all') params.set('template_id', templateFilter);
        const q = params.toString();
        const resp = await apiClient.get(`/automation/generated${q ? `?${q}` : ''}`);
        const body = resp.data?.data || resp.data || [];
        const rows = Array.isArray(body) ? body : (body.items || []);
        return rows.map(normaliseGenerated);
      } catch {
        return [];
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/automation/generated/${id}`),
    onSuccess: () => {
      toast.success('Document deleted');
      queryClient.invalidateQueries({ queryKey: ['automation', 'generated'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to delete'),
  });

  const [emailTarget, setEmailTarget] = useState<GeneratedDocument | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="All clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {(clients as any[]).map((c: any) => (
              <SelectItem key={c.id} value={c.id}>{c.displayName || c.display_name || c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={templateFilter} onValueChange={setTemplateFilter}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="All templates" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All templates</SelectItem>
            {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
      ) : generated.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No generated documents found.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y">
            {generated.map(doc => {
              const title = doc.templateName || doc.filename || 'Generated document';
              return (
                <div key={doc.id} className="flex items-center gap-3 p-3">
                  <div className="h-9 w-9 rounded bg-indigo-500/10 text-indigo-500 flex items-center justify-center shrink-0">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{title}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {doc.clientName || 'Unknown client'} · {formatDateTime(doc.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {doc.emailStatus === 'sent' && <Badge className="text-[10px] bg-green-500/10 text-green-600 border-green-500/30">Sent</Badge>}
                    {doc.emailStatus === 'failed' && <Badge className="text-[10px] bg-red-500/10 text-red-600 border-red-500/30">Failed</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => download(`/automation/generated/${doc.id}/download-pdf`)}>
                      <Download className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => download(`/automation/generated/${doc.id}/download-docx`)}>
                      <Download className="h-3.5 w-3.5 mr-1" /> Word
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEmailTarget(doc)}>
                      <Send className="h-3.5 w-3.5 mr-1" /> Email
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        if (confirm(`Delete "${title}" for ${doc.clientName || 'client'}? This cannot be undone.`)) {
                          deleteMutation.mutate(doc.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <SendEmailDialog
        open={!!emailTarget}
        onOpenChange={(o) => { if (!o) setEmailTarget(null); }}
        generatedId={emailTarget?.id || null}
        filename={emailTarget?.filename}
      />
    </div>
  );
}
